// 聊天记录分析服务 - 分析聊天记录，识别关键节点、情感高潮等
import { RealAIService } from './realAiService.js'
import settingsService from './settingsService.js'

class ChatAnalysisService {
  /**
   * 分析聊天记录，识别关键节点
   */
  async analyzeChatHistory(messages) {
    try {
      const languageConfig = settingsService.getLanguageModelConfig()
      
      if (!languageConfig.apiKey || !languageConfig.apiUrl) {
        // 降级到简单分析
        return this.simpleAnalysis(messages)
      }

      // 构建分析提示词
      const conversationText = messages
        .map(msg => `${msg.sender === 'user' ? '用户' : '角色'}：${msg.content}`)
        .join('\n')

      const prompt = `分析以下对话记录，识别关键节点：

${conversationText}

请识别：
1. 情感高潮点（情绪最强烈的地方）
2. 关键转折点（话题或关系发生重要变化的地方）
3. 有趣的互动（幽默、意外的对话）
4. 重要决策点（用户做出重要选择的地方）

请以JSON格式返回：
{
  "emotionalHighs": [
    {
      "messageId": "消息ID",
      "description": "为什么这是情感高潮",
      "intensity": 8
    }
  ],
  "turningPoints": [
    {
      "messageId": "消息ID",
      "description": "转折点描述",
      "significance": "高/中/低"
    }
  ],
  "interestingInteractions": [
    {
      "messageId": "消息ID",
      "description": "为什么有趣"
    }
  ],
  "decisionPoints": [
    {
      "messageId": "消息ID",
      "description": "决策内容"
    }
  ]
}`

      const response = await RealAIService.callLanguageModel(prompt)
      
      // 尝试解析JSON
      try {
        const jsonMatch = response.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          const parsed = JSON.parse(jsonMatch[0])
          return parsed
        }
      } catch (e) {
        console.warn('解析分析结果JSON失败，使用降级方案')
      }
      
      // 降级方案
      return this.simpleAnalysis(messages)
    } catch (error) {
      console.error('❌ 分析聊天记录失败:', error)
      return this.simpleAnalysis(messages)
    }
  }

  /**
   * 简单分析（降级方案）
   */
  simpleAnalysis(messages) {
    const emotionalHighs = []
    const turningPoints = []
    const interestingInteractions = []
    const decisionPoints = []

    // 分析消息长度和关键词
    messages.forEach((msg, index) => {
      const content = msg.content.toLowerCase()
      
      // 情感关键词
      const emotionKeywords = ['喜欢', '爱', '开心', '高兴', '难过', '生气', '害怕']
      if (emotionKeywords.some(k => content.includes(k))) {
        emotionalHighs.push({
          messageId: msg.id,
          description: '包含情感关键词',
          intensity: 7
        })
      }

      // 转折点：话题突然变化
      if (index > 0) {
        const prevContent = messages[index - 1].content.toLowerCase()
        const currentWords = content.split(/\s+/)
        const prevWords = prevContent.split(/\s+/)
        const commonWords = currentWords.filter(w => prevWords.includes(w))
        if (commonWords.length < 2 && content.length > 10) {
          turningPoints.push({
            messageId: msg.id,
            description: '话题转向',
            significance: '中'
          })
        }
      }

      // 有趣的互动：包含问号或感叹号
      if (content.includes('?') || content.includes('!')) {
        interestingInteractions.push({
          messageId: msg.id,
          description: '包含疑问或感叹'
        })
      }

      // 决策点：用户消息包含选择相关词汇
      if (msg.sender === 'user') {
        const decisionKeywords = ['选择', '决定', '想要', '希望', '应该']
        if (decisionKeywords.some(k => content.includes(k))) {
          decisionPoints.push({
            messageId: msg.id,
            description: '用户表达选择意愿'
          })
        }
      }
    })

    return {
      emotionalHighs: emotionalHighs.slice(0, 5),
      turningPoints: turningPoints.slice(0, 5),
      interestingInteractions: interestingInteractions.slice(0, 5),
      decisionPoints: decisionPoints.slice(0, 5)
    }
  }

  /**
   * 提取关键对话作为选项
   */
  extractKeyConversations(messages, analysis) {
    const keyMessages = []
    
    // 收集所有关键消息ID
    const keyIds = new Set()
    analysis.emotionalHighs.forEach(h => keyIds.add(h.messageId))
    analysis.turningPoints.forEach(t => keyIds.add(t.messageId))
    analysis.decisionPoints.forEach(d => keyIds.add(d.messageId))
    
    // 提取对应的消息
    messages.forEach(msg => {
      if (keyIds.has(msg.id) && msg.sender === 'user') {
        keyMessages.push(msg)
      }
    })
    
    return keyMessages
  }
}

// 导出单例
const chatAnalysisService = new ChatAnalysisService()

export default chatAnalysisService

